/**
 * Типы ошибок API и утилиты для их классификации.
 *
 * @see README.md — полная документация с примерами и инструкциями по настройке
 */

// ============================================================================
// Базовые типы
// ============================================================================

/**
 * API ошибка без привязки к полю (ошибка бизнес-логики).
 */
export interface IApiError<TCode extends string = string> {
  message: string;
  errors: Array<{
    code: TCode;
    message: string;
  }>;
}

/**
 * Ошибка валидации конкретного поля формы.
 */
export interface IValidationError<TKey extends string = string> {
  key: TKey;
  code: string;
  message: string;
}

/**
 * Ответ API с ошибками валидации полей.
 */
export interface IValidationApiError<TKey extends string = string> {
  message: string;
  errors: IValidationError<TKey>[];
}

// ============================================================================
// Type guards
// ============================================================================

/**
 * Проверяет, что ошибка — валидация полей (есть key).
 */
export function isValidationError(error: unknown): error is IValidationApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'errors' in error &&
    Array.isArray((error as IValidationApiError).errors) &&
    (error as IValidationApiError).errors.length > 0 &&
    'key' in (error as IValidationApiError).errors[0]
  );
}

/**
 * Проверяет, что ошибка — API/бизнес-ошибка (code без key).
 */
export function isApiError(error: unknown): error is IApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'errors' in error &&
    Array.isArray((error as IApiError).errors) &&
    (error as IApiError).errors.length > 0 &&
    'code' in (error as IApiError).errors[0] &&
    !('key' in (error as IApiError).errors[0])
  );
}

// ============================================================================
// Discriminated union для сервисов
// ============================================================================

/**
 * Результат классификации ошибки в сервисе.
 * Используется как rejectValue в thunk'ах.
 */
export type TServiceError<
  TValidation extends IValidationApiError = IValidationApiError,
  TApi extends IApiError = IApiError,
> = { type: 'validation'; data: TValidation } | { type: 'api'; data: TApi } | { type: 'unknown' };

/**
 * Упрощённый тип для использования в catch блоках компонентов.
 */
export type TServiceErrorUnknown = TServiceError<IValidationApiError, IApiError>;

/**
 * Классифицирует ответ API.
 * Используется в сервисах: `return Err(classifyApiError(response))`
 */
export function classifyApiError<TValidation extends IValidationApiError, TApi extends IApiError>(
  response: unknown,
): TServiceError<TValidation, TApi> {
  if (isValidationError(response)) {
    return { type: 'validation', data: response as TValidation };
  }

  if (isApiError(response)) {
    return { type: 'api', data: response as TApi };
  }

  return { type: 'unknown' };
}

// ============================================================================
// Хелперы для работы с кодами ошибок
// ============================================================================

/**
 * Извлекает первый код ошибки. Полезно для switch по коду.
 *
 * @example
 * const code = getErrorCode(error);
 * switch (code) {
 *   case 'hostNotAcceptedPublicOffer':
 *     // показать модалку с офертой
 *     break;
 *   case 'userBlocked':
 *     // редирект на страницу блокировки
 *     break;
 * }
 */
export function getErrorCode<TCode extends string>(error: IApiError<TCode | 'unknown'>): TCode | 'unknown' {
  return error.errors?.[0]?.code ?? 'unknown';
}

/**
 * Type guard для проверки конкретного кода ошибки.
 *
 * @example
 * if (isErrorCode(error, 'hostNotAcceptedPublicOffer')) {
 *   // TypeScript знает, что error.errors[0].code === 'hostNotAcceptedPublicOffer'
 *   showOfferModal();
 * }
 */
export function isErrorCode<TCode extends string>(error: IApiError<string>, code: TCode): error is IApiError<TCode> {
  return error.errors?.[0]?.code === code;
}
